import { Injectable } from '@angular/core';
import { DashboardData, PostData } from '@app-model/dashboard';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ProductService } from './product.service';
import { DateAdapter } from '@angular/material/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';

@Injectable({
	providedIn: 'root'
})
export class FilterService {
	private postDataSubject = new BehaviorSubject<PostData>(null);
	postData$: Observable<PostData> = this.postDataSubject.asObservable();

	private _isOpened: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	private selectedIdsSubject = new BehaviorSubject<DashboardData>(null);

	selectedIds$: Observable<DashboardData> = this.selectedIdsSubject.asObservable();

	private hasFiltersSubject = new BehaviorSubject<boolean>(false);
	hasFilters$: Observable<boolean> = this.hasFiltersSubject.asObservable();

	constructor(
		private readonly productService: ProductService,
		private readonly dateAdapter: DateAdapter<Date>,
		private http: HttpClient
	) {
		// Cria uma data local sem problemas de timezone
		const now = new Date();
		const localDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const today = this.dateAdapter.getValidDateOrNull(localDate);
		const yesterDay = this.dateAdapter.addCalendarDays(today, -1);
		const startDateObj = this.dateAdapter.addCalendarDays(yesterDay, -29);
		const endDateObj = yesterDay;

		const endDate = this.dateAdapter.getValidDateOrNull(endDateObj);
		const startDate = this.dateAdapter.getValidDateOrNull(startDateObj);

		this.productService.getProductInfo().subscribe(productInfo => {
			this.dateAdapter.setLocale(productInfo?.language);

			this.postDataSubject.next({
				cardholderIds: [],
				clientIds: [],
				end: this.formatDateToYMD(endDate),
				groupIds: [],
				ini: this.formatDateToYMD(startDate),
				productId: productInfo?.id
			});

			this.selectedIdsSubject.next({
				clients: [],
				groups: [],
				cardholders: []
			});
		});
	}

	public getCurrentSelectedIds(): DashboardData {
		return this.selectedIdsSubject.getValue();
	}
	public getCurrentPostData(): PostData {
		return this.postDataSubject.getValue();
	}
	public getCurrentHasFilters(): boolean {
		return this.hasFiltersSubject.getValue();
	}

	public updatePostData(data: PostData): void {
		this.postDataSubject.next(data);
	}

	public handleFilters(): Observable<PostData> {
		return this.postDataSubject.asObservable();
	}

	public openFilter(): void {
		this._isOpened.next(true);
	}

	public closeFilter(): void {
		this._isOpened.next(false);
	}

	public handleFilterStatus(): Observable<boolean> {
		return this._isOpened.asObservable();
	}

	public updateSelectedIds(selecteds: DashboardData): void {
		this.selectedIdsSubject.next(selecteds);
		this.updatePostDataFromSelectedIds(selecteds);
	}

	private formatDateToYMD(date: Date): string {
		if (!date) {
			return '';
		}
		// Usa os valores locais da data para evitar problemas de timezone
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	getDashboardFilterOptions(): Observable<DashboardData> {
		return this.http.get<DashboardData>(`${environment.api}/dashboard/filterOptions`).pipe(
			tap((response: DashboardData) => {
				this.hasFiltersSubject.next(
					response?.clients?.length > 1 || response?.groups?.length > 1 || response?.cardholders?.length > 1
				);
			})
		);
	}

	private updatePostDataFromSelectedIds(selecteds: DashboardData): void {
		const currentPostData = this.postDataSubject.getValue();

		const updatedPostData: PostData = {
			...currentPostData,
			clientIds: selecteds?.clients?.map(client => client.id) || [],
			groupIds: selecteds?.groups?.map(group => group.id) || [],
			cardholderIds: selecteds?.cardholders?.map(cardholder => cardholder.id) || []
		};

		this.updatePostData(updatedPostData);
	}
}
